import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { launchesApi } from "../api/endpoints";
import { useSettings } from "../hooks/useSettings";
import { recomputeDays } from "../lib/recomputeDays";
import {
  IcoArchive,
  IcoBoard,
  IcoCopy,
  IcoEdit,
  IcoPlus,
  IcoSearch,
  IcoTrash,
} from "../components/icons";
import type { Launch, LaunchStatus } from "../types/domain";
import { NewLaunchModal } from "./launches/NewLaunchModal";
import { EditLaunchModal } from "./launches/EditLaunchModal";
import { launchAvatarText } from "../lib/launchAvatar";
import { CanWrite } from "../components/CanWrite";
import { useCanWrite } from "../hooks/useCanWrite";

const STATUS_META: Record<
  LaunchStatus,
  { label: string; color: string; bg: string; dotColor: string; pulse?: boolean }
> = {
  draft: { label: "Черновик", color: "#7A849B", bg: "#F1F3F8", dotColor: "#A0A8BD" },
  active: { label: "Активный", color: "#0F766E", bg: "#CCFBF1", dotColor: "#14B8A6", pulse: true },
  completed: { label: "Завершён", color: "#3730A3", bg: "#E0E7FF", dotColor: "#6366F1" },
  archived: { label: "В архиве", color: "#6B7280", bg: "#F1F3F8", dotColor: "#9CA3AF" },
};

const TABS: Array<{ id: LaunchStatus; label: string }> = [
  { id: "active", label: "Активные" },
  { id: "draft", label: "Черновики" },
  { id: "completed", label: "Завершённые" },
  { id: "archived", label: "Архив" },
];

const launchDateSummary = (launch: Launch): string => {
  if (!launch.days?.length) return "—";
  const labelled = recomputeDays(launch.days, launch.d1StartDate);
  if (!labelled.length) return "—";
  const first = labelled[0]!;
  const last = labelled[labelled.length - 1]!;
  return `${first.date} — ${last.date} · ${labelled.length} дн.`;
};

const todayPlusDays = (offset: number) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

export function LaunchesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { update: updateSettings } = useSettings();

  const [tab, setTab] = useState<LaunchStatus>("active");
  const [query, setQuery] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [editLaunch, setEditLaunch] = useState<Launch | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setNewOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
  });
  const launches = launchesQuery.data ?? [];

  const counts = useMemo(
    () => ({
      active: launches.filter((l) => l.status === "active").length,
      draft: launches.filter((l) => l.status === "draft").length,
      completed: launches.filter((l) => l.status === "completed").length,
      archived: launches.filter((l) => l.status === "archived").length,
    }),
    [launches],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return launches
      .filter((l) => {
        if (l.status !== tab) return false;
        if (q && !l.name.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) =>
        tab === "active" || tab === "draft"
          ? a.d1StartDate.localeCompare(b.d1StartDate)
          : b.d1StartDate.localeCompare(a.d1StartDate),
      );
  }, [launches, tab, query]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["launches"] });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => launchesApi.patch(id, { status: "archived" }),
    onSuccess: invalidate,
  });
  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => launchesApi.patch(id, { status: "draft" }),
    onSuccess: invalidate,
  });
  const duplicateMutation = useMutation({
    mutationFn: (l: Launch) =>
      launchesApi.clone(l.id, {
        name: `Копия — ${l.name}`,
        d1StartDate: todayPlusDays(14),
        copyCards: true,
      }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => launchesApi.remove(id),
    onSuccess: invalidate,
  });

  const openLaunch = (id: string) => {
    updateSettings({ activeLaunchId: id });
    navigate("/board");
  };

  const handleDelete = (l: Launch) => {
    const yes = window.confirm(
      `Удалить запуск «${l.name}»? Все карточки и черновики этого запуска будут удалены.`,
    );
    if (yes) deleteMutation.mutate(l.id);
  };

  return (
    <div className="launches-page">
      <div className="launches-head">
        <div>
          <h1 className="launches-title">Запуски</h1>
          <div className="launches-subtitle">
            <b>{counts.active}</b> активных · <b>{counts.draft}</b> в работе ·{" "}
            <b>{counts.completed + counts.archived}</b> в истории
          </div>
        </div>
        <div className="launches-head-right">
          <div className="search" style={{ minWidth: 240 }}>
            <IcoSearch size={14} stroke="#7A849B" />
            <input
              placeholder="Поиск запуска..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <CanWrite>
            <button type="button" className="btn-primary" onClick={() => setNewOpen(true)}>
              <IcoPlus size={14} stroke="white" sw={2.5} /> Новый запуск
            </button>
          </CanWrite>
        </div>
      </div>

      <div className="launches-tabs">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.id}
            className={"launches-tab" + (tab === t.id ? " active" : "")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            <span className="launches-tab-count">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {launchesQuery.isLoading ? (
        <div className="launches-empty">
          <p>Загружаем…</p>
        </div>
      ) : launchesQuery.isError ? (
        <div className="launches-empty">
          <h3>Не удалось загрузить запуски</h3>
          <p>{(launchesQuery.error as Error).message}</p>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState tab={tab} onNew={() => setNewOpen(true)} />
      ) : (
        <div className="launches-grid">
          {filtered.map((l) => (
            <LaunchCard
              key={l.id}
              launch={l}
              onOpen={() => openLaunch(l.id)}
              onEdit={() => setEditLaunch(l)}
              onArchive={() => archiveMutation.mutate(l.id)}
              onUnarchive={() => unarchiveMutation.mutate(l.id)}
              onDuplicate={() => duplicateMutation.mutate(l)}
              onDelete={() => handleDelete(l)}
            />
          ))}
        </div>
      )}

      {newOpen && (
        <CanWrite fallback={null}>
          <NewLaunchModal
            existingLaunches={launches.filter((l) => l.status !== "archived")}
            onClose={() => setNewOpen(false)}
            onCreated={(launch) => {
              setNewOpen(false);
              openLaunch(launch.id);
            }}
          />
        </CanWrite>
      )}

      {editLaunch && (
        <EditLaunchModal
          launch={editLaunch}
          onClose={() => setEditLaunch(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ tab, onNew }: { tab: LaunchStatus; onNew: () => void }) {
  const canWrite = useCanWrite();
  const titles: Record<LaunchStatus, string> = {
    active: "Нет активных запусков",
    draft: "Нет черновиков",
    completed: "Ничего не завершено",
    archived: "Архив пуст",
  };
  const hints: Record<LaunchStatus, string> = {
    active: "Создайте первый запуск или восстановите из архива.",
    draft: "Черновики — запуски, которые вы ещё планируете.",
    completed: "Сюда попадают запуски, даты которых прошли.",
    archived: "Сюда можно убрать запуски, чтобы не мешали в списке.",
  };
  return (
    <div className="launches-empty">
      <div className="launches-empty-icon">
        <IcoBoard size={24} stroke="var(--text-muted)" />
      </div>
      <h3>{titles[tab]}</h3>
      <p>{hints[tab]}</p>
      {tab === "active" && canWrite && (
        <button type="button" className="btn-primary" onClick={onNew} style={{ marginTop: 14 }}>
          <IcoPlus size={14} stroke="white" sw={2.5} /> Создать запуск
        </button>
      )}
    </div>
  );
}

interface LaunchCardProps {
  launch: Launch;
  onOpen: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function LaunchCard({
  launch,
  onOpen,
  onEdit,
  onArchive,
  onUnarchive,
  onDuplicate,
  onDelete,
}: LaunchCardProps) {
  const meta = STATUS_META[launch.status];
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const canWrite = useCanWrite();
  const dateSummary = launchDateSummary(launch);
  const cardCount = launch._count?.cards ?? 0;
  const taskCount = launch._count?.tasks ?? 0;
  const taskOverdue = launch.tasksOverdue ?? 0;

  return (
    <div className="launch-card" onClick={onOpen}>
      <div className="launch-card-head">
        <div
          className="launch-card-emoji"
          style={{ background: launch.color ?? "var(--accent)" }}
        >
          {launchAvatarText(launch)}
        </div>
        <div className="launch-card-title-wrap">
          <h3 className="launch-card-title">{launch.name}</h3>
          <div className="launch-card-meta">{dateSummary}</div>
        </div>
        <div
          className="launch-card-menu-wrap"
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="launch-card-menu-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Действия"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="5" cy="12" r="1.3" />
              <circle cx="12" cy="12" r="1.3" />
              <circle cx="19" cy="12" r="1.3" />
            </svg>
          </button>
          {menuOpen && (
            <div className="col-dropdown launch-card-dropdown">
              <button
                type="button"
                className="col-dropdown-item"
                onClick={() => {
                  setMenuOpen(false);
                  onOpen();
                }}
              >
                <IcoBoard size={13} /> Открыть доску
              </button>
              <button
                type="button"
                className="col-dropdown-item"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                <IcoEdit size={13} /> {canWrite ? "Редактировать" : "Подробнее"}
              </button>
              {canWrite && (
                <>
                  <button
                    type="button"
                    className="col-dropdown-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onDuplicate();
                    }}
                  >
                    <IcoCopy size={13} /> Клонировать
                  </button>
                  <div className="col-dropdown-sep" />
                  {launch.status !== "archived" ? (
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={() => {
                        setMenuOpen(false);
                        onArchive();
                      }}
                    >
                      <IcoArchive size={13} /> В архив
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={() => {
                        setMenuOpen(false);
                        onUnarchive();
                      }}
                    >
                      <IcoArchive size={13} /> Вернуть из архива
                    </button>
                  )}
                  <button
                    type="button"
                    className="col-dropdown-item col-dropdown-danger"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                  >
                    <IcoTrash size={13} /> Удалить
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {launch.description && <p className="launch-card-desc">{launch.description}</p>}

      <div className="launch-card-stats">
        <div className="launch-stat">
          <span className="launch-stat-label">Карточек</span>
          <b>{cardCount}</b>
        </div>
        <div className="launch-stat">
          <span className="launch-stat-label">Задач</span>
          <b>
            {taskCount}
            {taskOverdue > 0 && (
              <span className="launch-stat-overdue" title="Просрочено">
                {" "}· ⚠ {taskOverdue}
              </span>
            )}
          </b>
        </div>
        <div className="launch-stat">
          <span className="launch-stat-label">Цель</span>
          <b>{launch.goal || "—"}</b>
        </div>
        {launch.result && (
          <div className="launch-stat launch-stat-result">
            <span className="launch-stat-label">Результат</span>
            <b>{launch.result}</b>
          </div>
        )}
        <div className="launch-stat launch-stat-aud">
          <span className="launch-stat-label">Аудитория</span>
          <b>{launch.audience || "—"}</b>
        </div>
      </div>

      <div className="launch-card-foot">
        <span
          className="launch-card-status"
          style={{ color: meta.color, background: meta.bg }}
        >
          <span
            className="status-dot"
            style={{
              background: meta.dotColor,
              animation: meta.pulse ? "livePulse 1.8s ease-in-out infinite" : "none",
            }}
          />
          {meta.label}
        </span>
        <button
          type="button"
          className="launch-card-open"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Открыть →
        </button>
      </div>
    </div>
  );
}
